import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import { stylesForType, isStyleValidForType } from '../artistStyleUtils';

// ── Columns available for bulk editing (picklist fields) ──────────────────────
const EDITABLE_FIELDS = [
  { key: 'artist_type_id',  label: 'Type',  picklistKey: 'artist_types'  },
  { key: 'artist_style_id', label: 'Genre', picklistKey: 'artist_styles' },
  { key: 'description',     label: 'Bio',   picklistKey: null            },
];

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 24px',
    color: '#e0e0e0',
    fontFamily: 'sans-serif',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#bb86fc',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '0 0 16px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '24px',
  },
  title: {
    fontSize: '26px',
    fontWeight: 'bold',
    color: '#bb86fc',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: '#777',
    margin: 0,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '18px',
  },
  searchInput: {
    backgroundColor: '#1e1e2e',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#e0e0e0',
    padding: '7px 12px',
    fontSize: '13px',
    width: '220px',
    outline: 'none',
  },
  filterSelect: {
    backgroundColor: '#1e1e2e',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#e0e0e0',
    padding: '7px 10px',
    fontSize: '13px',
    outline: 'none',
    cursor: 'pointer',
  },
  dirtyBadge: {
    backgroundColor: '#bb86fc22',
    border: '1px solid #bb86fc',
    borderRadius: '20px',
    color: '#bb86fc',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  saveBtn: {
    backgroundColor: '#bb86fc',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
    padding: '8px 20px',
    transition: 'opacity 0.15s',
  },
  discardBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #444',
    borderRadius: '6px',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '8px 16px',
  },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: '10px',
    border: '1px solid #2a2a2a',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    backgroundColor: '#1a1a2e',
    color: '#777',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '10px 14px',
    textAlign: 'left',
    borderBottom: '1px solid #2a2a2a',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
  },
  td: {
    padding: '9px 14px',
    borderBottom: '1px solid #1e1e2e',
    verticalAlign: 'middle',
  },
  rowDirty: {
    backgroundColor: '#1e1a2e',
  },
  rowNormal: {
    backgroundColor: 'transparent',
  },
  artistName: {
    color: '#e0e0e0',
    fontWeight: '500',
  },
  artistMeta: {
    color: '#555',
    fontSize: '11px',
    marginTop: '2px',
  },
  changedIndicator: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#bb86fc',
    marginRight: '6px',
    verticalAlign: 'middle',
  },
  emptyCell: {
    color: '#444',
    fontStyle: 'italic',
  },
  bioPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '5px 8px',
    borderRadius: '5px',
    border: '1px solid transparent',
    fontSize: '12px',
    maxWidth: '240px',
    transition: 'border-color 0.15s, background-color 0.15s',
  },
  bioPreviewText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 4px 0',
    fontSize: '12px',
    color: '#555',
  },
  pageBtn: {
    backgroundColor: '#1e1e2e',
    border: '1px solid #333',
    borderRadius: '5px',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '5px 12px',
  },
  // ── Modal ──
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px',
  },
  modalBox: {
    backgroundColor: '#1e1e2e',
    border: '1px solid #333',
    borderRadius: '12px',
    padding: '28px',
    width: '100%',
    maxWidth: '640px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  modalTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#bb86fc',
    margin: 0,
  },
  modalSubtitle: {
    fontSize: '12px',
    color: '#555',
    margin: '2px 0 0 0',
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    color: '#555',
    cursor: 'pointer',
    fontSize: '20px',
    lineHeight: 1,
    padding: '0',
    flexShrink: 0,
  },
  modalTextarea: {
    backgroundColor: '#121212',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#e0e0e0',
    fontSize: '13px',
    lineHeight: '1.6',
    outline: 'none',
    padding: '12px',
    resize: 'vertical',
    width: '100%',
    minHeight: '180px',
    boxSizing: 'border-box',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  toast: {
    position: 'fixed',
    bottom: '28px',
    right: '28px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    zIndex: 9999,
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
  },
  toastSuccess: {
    backgroundColor: '#1a3d2b',
    border: '1px solid #2d7a4f',
    color: '#4ade80',
  },
  toastError: {
    backgroundColor: '#3d1a1a',
    border: '1px solid #7a2d2d',
    color: '#f87171',
  },
};

const PAGE_SIZE = 50;

export default function AdminBulkEditArtists() {
  const navigate = useNavigate();

  // ── Data state ──────────────────────────────────────────────────────────────
  const [artists, setArtists]     = useState([]);
  const [picklists, setPicklists] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // ── Edit state ──────────────────────────────────────────────────────────────
  const [edits, setEdits]   = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState(null);

  // ── Bio modal state ─────────────────────────────────────────────────────────
  const [bioModal, setBioModal] = useState(null); // { artist } | null
  const [bioText, setBioText]   = useState('');

  // ── Filter / sort / page state ──────────────────────────────────────────────
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState('');
  const [filterStyle, setFilterStyle] = useState('');
  const [sortKey, setSortKey]         = useState('name');
  const [sortDir, setSortDir]         = useState('asc');
  const [page, setPage]               = useState(1);

  // ── Load data ───────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      axios.get(`${config.API_URL}/artists`),
      axios.get(`${config.API_URL}/picklists`),
    ]).then(([artistsRes, picklistsRes]) => {
      setArtists(artistsRes.data);
      setPicklists(picklistsRes.data);
      setLoading(false);
    }).catch(() => {
      setError('Failed to load data. Please refresh.');
      setLoading(false);
    });
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getAuthHeader = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const showToast = useCallback((message, type = 'success', ms = 3500) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), ms);
  }, []);

  const picklistOptions = useCallback((key) => {
    if (!picklists) return [];
    return picklists[key] || [];
  }, [picklists]);

  // ── Compute effective value for a field (edit override or original) ─────────
  const effectiveVal = useCallback((artist, fieldKey) => {
    if (edits[artist.id] && edits[artist.id][fieldKey] !== undefined) {
      return edits[artist.id][fieldKey];
    }
    return artist[fieldKey] ?? '';
  }, [edits]);

  // ── Track if a specific cell is dirty ──────────────────────────────────────
  const isCellDirty = useCallback((artist, fieldKey) => {
    if (!edits[artist.id]) return false;
    if (edits[artist.id][fieldKey] === undefined) return false;
    const original = artist[fieldKey] ?? '';
    return String(edits[artist.id][fieldKey]) !== String(original);
  }, [edits]);

  const dirtyCount = useMemo(() =>
    Object.keys(edits).filter(id => {
      const artist = artists.find(a => a.id === parseInt(id));
      if (!artist) return false;
      return EDITABLE_FIELDS.some(f => isCellDirty(artist, f.key));
    }).length,
    [edits, artists, isCellDirty]
  );

  // ── Genre options for a row: styles allowed for its effective Type ─────────
  // (2026-10-12 dependent picklist). A legacy style that doesn't fit the type
  // stays listed, flagged, so the select shows the real value.
  const styleOptionsFor = useCallback((artist) => {
    if (!picklists) return [];
    const typeId = effectiveVal(artist, 'artist_type_id');
    const list = stylesForType(picklists.artist_styles, picklists.artist_types, typeId);
    const current = effectiveVal(artist, 'artist_style_id');
    const cur = current === '' || current === null ? null : parseInt(current);
    if (cur && !list.some(o => o.id === cur)) {
      const saved = picklists.artist_styles.find(o => o.id === cur);
      if (saved) return [...list, { ...saved, value: `${saved.value} (doesn't match type)` }];
    }
    return list;
  }, [picklists, effectiveVal]);

  // ── Handle inline cell change (picklist dropdowns) ──────────────────────────
  // Changing Type clears a Genre the new type can't use.
  const handleCellChange = useCallback((artistId, fieldKey, newValue) => {
    setEdits(prev => {
      const parsed = newValue === '' ? null : parseInt(newValue);
      const rowEdits = { ...(prev[artistId] || {}), [fieldKey]: parsed };
      if (fieldKey === 'artist_type_id' && picklists) {
        const artist = artists.find(a => a.id === artistId) || {};
        const style = rowEdits.artist_style_id !== undefined ? rowEdits.artist_style_id : (artist.artist_style_id ?? null);
        if (style !== null && !isStyleValidForType(picklists.artist_styles, picklists.artist_types, parsed, style)) {
          rowEdits.artist_style_id = null;
        }
      }
      return { ...prev, [artistId]: rowEdits };
    });
  }, [picklists, artists]);

  // ── Bio modal handlers ──────────────────────────────────────────────────────
  const openBioModal = useCallback((artist) => {
    const current = (edits[artist.id]?.description !== undefined)
      ? edits[artist.id].description
      : (artist.description || '');
    setBioText(current);
    setBioModal({ artist });
  }, [edits]);

  const closeBioModal = useCallback(() => {
    setBioModal(null);
    setBioText('');
  }, []);

  const saveBioModal = useCallback(() => {
    if (!bioModal) return;
    const { artist } = bioModal;
    setEdits(prev => ({
      ...prev,
      [artist.id]: {
        ...(prev[artist.id] || {}),
        description: bioText.trim() || null,
      },
    }));
    closeBioModal();
  }, [bioModal, bioText, closeBioModal]);

  // Close modal on Escape key
  useEffect(() => {
    if (!bioModal) return;
    const handler = (e) => { if (e.key === 'Escape') closeBioModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [bioModal, closeBioModal]);

  // ── Discard all pending edits ───────────────────────────────────────────────
  const handleDiscard = useCallback(() => {
    setEdits({});
  }, []);

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const updates = [];
    Object.entries(edits).forEach(([idStr, changes]) => {
      const artistId = parseInt(idStr);
      const artist = artists.find(a => a.id === artistId);
      if (!artist) return;
      const dirtyFields = {};

      // Picklist fields
      EDITABLE_FIELDS.filter(f => f.picklistKey).forEach(f => {
        if (changes[f.key] !== undefined) {
          const original = artist[f.key] ?? null;
          const next = changes[f.key] ?? null;
          if (String(next) !== String(original)) {
            dirtyFields[f.key] = next;
          }
        }
      });

      // Bio (text field — compare as strings)
      if (changes.description !== undefined) {
        const original = artist.description ?? null;
        const next = changes.description ?? null;
        if (next !== original) {
          dirtyFields.description = next;
        }
      }

      if (Object.keys(dirtyFields).length > 0) {
        updates.push({ id: artistId, ...dirtyFields });
      }
    });

    if (updates.length === 0) {
      showToast('No changes to save.', 'error');
      return;
    }

    setSaving(true);
    try {
      const headers = await getAuthHeader();
      await axios.put(
        `${config.API_URL}/admin/artists/bulk`,
        { updates },
        { headers }
      );

      // Merge saved changes back into local artist list (refresh the display
      // labels too, so sorting/filters reflect the new type/genre)
      const typeLabel  = id => (picklists?.artist_types  || []).find(t => t.id === id)?.value ?? null;
      const styleLabel = id => (picklists?.artist_styles || []).find(t => t.id === id)?.value ?? null;
      setArtists(prev => prev.map(a => {
        const update = updates.find(u => u.id === a.id);
        if (!update) return a;
        const merged = { ...a, ...update };
        if ('artist_type_id'  in update) merged.artist_type  = typeLabel(update.artist_type_id);
        if ('artist_style_id' in update) merged.artist_style = styleLabel(update.artist_style_id);
        return merged;
      }));

      setEdits({});
      showToast(`Saved ${updates.length} artist${updates.length !== 1 ? 's' : ''} successfully.`, 'success');
    } catch (err) {
      const data = err.response?.data || {};
      if (data.error === 'invalid_style' && Array.isArray(data.invalid)) {
        const names = data.invalid.slice(0, 5).map(r => `${r.name} — ${r.message}`).join(' · ');
        const more = data.invalid.length > 5 ? ` · +${data.invalid.length - 5} more` : '';
        showToast(`${data.message} ${names}${more}`, 'error', 10000);
      } else {
        showToast(data.error || 'Save failed. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  }, [edits, artists, picklists, getAuthHeader, showToast]);

  // ── Sort handler ─────────────────────────────────────────────────────────────
  const handleSort = useCallback((key) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return key;
      }
      setSortDir('asc');
      return key;
    });
    setPage(1);
  }, []);

  // ── Filtered + sorted data ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let rows = artists;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.city || '').toLowerCase().includes(q)
      );
    }

    if (filterType)  rows = rows.filter(a => String(a.artist_type_id) === filterType);
    if (filterStyle) rows = rows.filter(a => String(a.artist_style_id) === filterStyle);

    rows = [...rows].sort((a, b) => {
      let va, vb;
      if (sortKey === 'name')       { va = a.name; vb = b.name; }
      else if (sortKey === 'city')  { va = a.city || ''; vb = b.city || ''; }
      else if (sortKey === 'type')  { va = a.artist_type || ''; vb = b.artist_type || ''; }
      else if (sortKey === 'style') { va = a.artist_style || ''; vb = b.artist_style || ''; }
      else if (sortKey === 'shows') { va = parseInt(a.total_shows) || 0; vb = parseInt(b.total_shows) || 0; }
      else { va = a.name; vb = b.name; }

      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return rows;
  }, [artists, search, filterType, filterStyle, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, filterType, filterStyle]);

  const sortIcon = (key) => {
    if (sortKey !== key) return <span style={{ color: '#333', marginLeft: '4px' }}>⇅</span>;
    return <span style={{ color: '#bb86fc', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, textAlign: 'center', paddingTop: '80px', color: '#555' }}>
      Loading artists…
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, textAlign: 'center', paddingTop: '80px', color: '#f87171' }}>
      {error}
    </div>
  );

  return (
    <div style={S.page}>
      {/* Back */}
      <button style={S.backBtn} onClick={() => navigate('/admin')}>
        ← Admin Portal
      </button>

      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Bulk Edit Artists</h1>
          <p style={S.subtitle}>
            Edit genre, type, bio, and other fields across multiple artists. Changes are staged until you hit Save.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <input
          style={S.searchInput}
          placeholder="Search by name or city…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          style={S.filterSelect}
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
        >
          <option value="">All Types</option>
          {picklistOptions('artist_types').map(o => (
            <option key={o.id} value={String(o.id)}>{o.value}</option>
          ))}
        </select>
        <select
          style={S.filterSelect}
          value={filterStyle}
          onChange={e => setFilterStyle(e.target.value)}
        >
          <option value="">All Genres</option>
          {picklistOptions('artist_styles').map(o => (
            <option key={o.id} value={String(o.id)}>{o.value}</option>
          ))}
        </select>

        <span style={{ flex: 1 }} />

        {dirtyCount > 0 && (
          <>
            <span style={S.dirtyBadge}>{dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}</span>
            <button style={S.discardBtn} onClick={handleDiscard} disabled={saving}>
              Discard
            </button>
            <button
              style={{ ...S.saveBtn, opacity: saving ? 0.6 : 1 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : `Save ${dirtyCount} Record${dirtyCount !== 1 ? 's' : ''}`}
            </button>
          </>
        )}
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th} onClick={() => handleSort('name')}>
                Artist {sortIcon('name')}
              </th>
              <th style={S.th} onClick={() => handleSort('city')}>
                City {sortIcon('city')}
              </th>
              <th style={S.th} onClick={() => handleSort('type')}>
                Type {sortIcon('type')}
              </th>
              <th style={S.th} onClick={() => handleSort('style')}>
                Genre {sortIcon('style')}
              </th>
              <th style={S.th}>
                Bio
              </th>
              <th style={{ ...S.th, textAlign: 'right' }} onClick={() => handleSort('shows')}>
                Shows {sortIcon('shows')}
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ ...S.td, textAlign: 'center', color: '#444', padding: '40px' }}>
                  No artists match your filters.
                </td>
              </tr>
            ) : pageRows.map(artist => {
              const hasDirtyRow = EDITABLE_FIELDS.some(f => isCellDirty(artist, f.key));
              const bioValue    = effectiveVal(artist, 'description');
              const bioDirty    = isCellDirty(artist, 'description');

              return (
                <tr key={artist.id} style={hasDirtyRow ? S.rowDirty : S.rowNormal}>

                  {/* Artist name */}
                  <td style={S.td}>
                    <div style={S.artistName}>
                      {hasDirtyRow && <span style={S.changedIndicator} title="Unsaved changes" />}
                      {artist.name}
                    </div>
                    <div style={S.artistMeta}>{artist.slug}</div>
                  </td>

                  {/* City (read-only) */}
                  <td style={S.td}>
                    {artist.city ? (
                      <span>{artist.city}{artist.state ? `, ${artist.state}` : ''}{artist.country ? `, ${artist.country}` : ''}</span>
                    ) : (
                      <span style={S.emptyCell}>—</span>
                    )}
                  </td>

                  {/* Type (editable) */}
                  <td style={S.td}>
                    <EditCell
                      artist={artist}
                      fieldKey="artist_type_id"
                      options={picklistOptions('artist_types')}
                      value={effectiveVal(artist, 'artist_type_id')}
                      dirty={isCellDirty(artist, 'artist_type_id')}
                      originalLabel={artist.artist_type}
                      onChange={handleCellChange}
                    />
                  </td>

                  {/* Genre (editable) */}
                  <td style={S.td}>
                    <EditCell
                      artist={artist}
                      fieldKey="artist_style_id"
                      options={styleOptionsFor(artist)}
                      value={effectiveVal(artist, 'artist_style_id')}
                      dirty={isCellDirty(artist, 'artist_style_id')}
                      originalLabel={artist.artist_style}
                      onChange={handleCellChange}
                    />
                  </td>

                  {/* Bio (modal) */}
                  <td style={{ ...S.td, maxWidth: '240px' }}>
                    <div
                      style={{
                        ...S.bioPreview,
                        color: bioValue ? (bioDirty ? '#bb86fc' : '#e0e0e0') : '#444',
                        fontStyle: bioValue ? 'normal' : 'italic',
                        borderColor: 'transparent',
                      }}
                      onClick={() => openBioModal(artist)}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#333';
                        e.currentTarget.style.backgroundColor = '#1a1a2e';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      title="Click to edit bio"
                    >
                      <span style={S.bioPreviewText}>
                        {bioValue ? bioValue : '— No bio —'}
                      </span>
                      <span style={{ color: '#444', fontSize: '10px', flexShrink: 0 }}>✎</span>
                    </div>
                  </td>

                  {/* Shows (read-only) */}
                  <td style={{ ...S.td, textAlign: 'right', color: '#777' }}>
                    {artist.total_shows || 0}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={S.pagination}>
        <span>
          Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
          {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} artists
        </span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            style={{ ...S.pageBtn, opacity: page <= 1 ? 0.4 : 1 }}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            ← Prev
          </button>
          <span style={{ color: '#aaa' }}>Page {page} of {totalPages}</span>
          <button
            style={{ ...S.pageBtn, opacity: page >= totalPages ? 0.4 : 1 }}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ ...S.toast, ...(toast.type === 'success' ? S.toastSuccess : S.toastError) }}>
          {toast.message}
        </div>
      )}

      {/* Bio Modal */}
      {bioModal && (
        <div style={S.modalOverlay} onClick={closeBioModal}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <p style={S.modalTitle}>{bioModal.artist.name}</p>
                <p style={S.modalSubtitle}>Edit bio / description</p>
              </div>
              <button style={S.modalCloseBtn} onClick={closeBioModal}>×</button>
            </div>

            <textarea
              style={S.modalTextarea}
              value={bioText}
              onChange={e => setBioText(e.target.value)}
              placeholder="Enter artist bio…"
              autoFocus
            />

            <div style={S.modalFooter}>
              <button style={S.discardBtn} onClick={closeBioModal}>
                Cancel
              </button>
              <button style={S.saveBtn} onClick={saveBioModal}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── EditCell ──────────────────────────────────────────────────────────────────
// Renders a read-only label by default; on click switches to a <select>.
// On blur it returns to label mode.
function EditCell({ artist, fieldKey, options, value, dirty, originalLabel, onChange }) {
  const [editing, setEditing] = useState(false);

  const selectedLabel = useMemo(() => {
    if (value === '' || value === null || value === undefined) return null;
    const match = options.find(o => o.id === parseInt(value));
    return match ? match.value : originalLabel;
  }, [value, options, originalLabel]);

  const handleBlur  = () => setEditing(false);

  const handleChange = (e) => {
    onChange(artist.id, fieldKey, e.target.value);
  };

  const selectStyle = {
    backgroundColor: dirty ? '#1a1228' : '#121212',
    border: `1px solid ${dirty ? '#bb86fc' : '#333'}`,
    borderRadius: '5px',
    color: '#e0e0e0',
    padding: '5px 8px',
    fontSize: '12px',
    width: '100%',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '150px',
  };

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    padding: '5px 8px',
    borderRadius: '5px',
    border: '1px solid transparent',
    fontSize: '12px',
    color: selectedLabel ? (dirty ? '#bb86fc' : '#e0e0e0') : '#444',
    fontStyle: selectedLabel ? 'normal' : 'italic',
    transition: 'border-color 0.15s, background-color 0.15s',
    minWidth: '150px',
  };

  if (editing) {
    return (
      <select
        autoFocus
        style={selectStyle}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={handleChange}
        onBlur={handleBlur}
      >
        <option value="">— None —</option>
        {options.map(o => (
          <option key={o.id} value={String(o.id)}>{o.value}</option>
        ))}
      </select>
    );
  }

  return (
    <div
      style={labelStyle}
      onClick={() => setEditing(true)}
      tabIndex={0}
      title="Click to edit"
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#333';
        e.currentTarget.style.backgroundColor = '#1a1a2e';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {selectedLabel || '—'}
      <span style={{ marginLeft: 'auto', color: '#444', fontSize: '10px' }}>✎</span>
    </div>
  );
}
